/** @format */

import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, map, switchMap, throwError } from 'rxjs';
import {
  Checkout,
  ExpressCheckoutAddressType,
  IAddToExpressBuyNowCartRequest,
  CommerceAddress,
  ContactForm,
  DirectCheckoutItems,
} from '@shared/data-access/interfaces';

import { CheckoutDirectDao } from './checkout-direct.dao';
import { ExpressCheckoutDao } from './express-checkout.dao';

export enum CheckoutEndpointFlow {
  EXPRESS = 'express',
  DIRECT = 'direct',
}

// Neither item shape is derivable from the other, so the caller passes the one its flow needs.
export type CheckoutCreateRequest = IAddToExpressBuyNowCartRequest | DirectCheckoutItems;

/**
 * Routes each checkout action to the /express or /direct endpoint set. The UI sets the flow
 * before a checkout exists, since create cannot be inferred from state; it defaults to EXPRESS.
 * `orderId` is ignored on express paths, which read it from their cookie. Direct-only actions
 * (gift card, proxy pickup, refresh) are absent — callers use CheckoutDirectDao for those.
 */
@Injectable({
  providedIn: 'root',
})
export class CheckoutSwitchingService {
  private readonly checkoutDirectDao = inject(CheckoutDirectDao);
  private readonly expressCheckoutDao = inject(ExpressCheckoutDao);

  private readonly _flow = signal<CheckoutEndpointFlow>(CheckoutEndpointFlow.EXPRESS);

  readonly flow = this._flow.asReadonly();
  readonly isDirect = computed(() => this._flow() === CheckoutEndpointFlow.DIRECT);

  setFlow(flow: CheckoutEndpointFlow): void {
    this._flow.set(flow);
  }

  // Express reads its order id from a cookie; direct takes it explicitly and has none to fall
  // back on, so a missing id is an error rather than a request to /direct/undefined/...
  private missingOrderId$(action: string): Observable<never> {
    return throwError(() => new Error(`Cannot ${action}: no active direct checkout.`));
  }

  createCheckout$(request: CheckoutCreateRequest): Observable<Checkout | null> {
    if (this.isDirect()) {
      return this.checkoutDirectDao.createCheckout(request as DirectCheckoutItems);
    }

    return this.expressCheckoutDao
      .addToExpressBuyNowCart$(request as IAddToExpressBuyNowCartRequest)
      .pipe(
        switchMap(() => this.expressCheckoutDao.createExpressCheckout$()),
        map((response) => response?.checkout ?? null),
      );
  }

  addAddress$(
    orderId: string | undefined,
    contact: ContactForm,
    address: CommerceAddress,
    addressType: ExpressCheckoutAddressType = ExpressCheckoutAddressType.SHIPPING_BILLING,
  ): Observable<Checkout | null> {
    if (this.isDirect()) {
      if (!orderId) {
        return this.missingOrderId$('add an address');
      }
      return this.checkoutDirectDao.saveAddress(
        orderId,
        this.expressCheckoutDao.toAddressRequest(contact, address),
        addressType as 'SHIPPING' | 'BILLING' | 'SHIPPINGBILLING',
      );
    }

    return this.expressCheckoutDao.addExpressAddress$(contact, address, addressType);
  }

  applyPromotion$(orderId: string, promotionCode: string): Observable<Checkout> {
    if (this.isDirect()) {
      return this.checkoutDirectDao.applyPromotion(orderId, promotionCode);
    }

    return this.expressCheckoutDao.applyExpressPromotionOrRewardCode$(orderId, promotionCode);
  }

  removePromotion$(orderId: string, promotionCode: string): Observable<Checkout> {
    if (this.isDirect()) {
      return this.checkoutDirectDao.deletePromotion(orderId, promotionCode);
    }

    return this.expressCheckoutDao.removeExpressPromotionOrRewardCode$(orderId, promotionCode);
  }

  submitCheckout$(orderId: string | undefined): Observable<Checkout | null> {
    if (this.isDirect()) {
      if (!orderId) {
        return this.missingOrderId$('submit');
      }
      return this.checkoutDirectDao.submitCheckout(orderId);
    }

    return this.expressCheckoutDao.submitExpressCheckout$();
  }

  getSubmittedCheckout$(orderId: string | undefined): Observable<Checkout | null> {
    if (this.isDirect()) {
      if (!orderId) {
        return this.missingOrderId$('read the submitted checkout');
      }
      return this.checkoutDirectDao.getSubmittedCheckout(orderId);
    }

    return this.expressCheckoutDao.getSubmittedExpressCheckout$();
  }
}
